/**
 * Fire-and-forget event logger for video meeting lifecycle events.
 *
 * Posts to /api/video/room/log-meeting-event/. The wrapper adds common
 * context (meetingId, roomId, userId, timestamp, userAgent) automatically
 * so call sites only pass the event-specific fields.
 *
 * Accepts meeting_id as the primary key — room_id is optional because
 * some events (device_permission_denied, device_start_failed) fire
 * before the user has joined a room.
 */
import { apiRequest } from "@core/services/api/apiRequest";
import { VIDEO_API } from "../videoMeeting.constants";
import { getTrackingMetadata } from "./getTrackingMetadata";

export type MeetingEventPayload = {
  event_type: string;
  meeting_id: number | string | null;
  room_id?: number | string | null;
  user?: { id: number } | null;
  [key: string]: unknown;
};

export async function logMeetingEvent(payload: MeetingEventPayload): Promise<void> {
  try {
    const trackingMetadata = getTrackingMetadata(
      payload.user ? { id: payload.user.id } : null,
    );

    await apiRequest(VIDEO_API.LOG_MEETING_EVENT, "POST", {
      event_type: payload.event_type,
      meeting_id: payload.meeting_id,
      room_id: payload.room_id ?? null,
      timestamp: Date.now(),
      ...trackingMetadata,
      // Spread remaining event-specific fields (excluding keys already set)
      ...Object.fromEntries(
        Object.entries(payload).filter(
          ([k]) => !["event_type", "meeting_id", "room_id", "user"].includes(k),
        ),
      ),
    });
  } catch {
    // Fire-and-forget — never let logging failures affect the meeting
  }
}
